import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '../Box';

/**
 * Used in: Partner Portal, EC Bookings
 * NOTE: tabs array should not have 'null' as the value for any tab
 */
export const TabBar = ({ className, style, tabs, value, onSelect, disableScroll = false }) => {
  const [hoveredTab, setHoveredTab] = useState(null);
  const isFirstRender = useRef(true);

  const tabRefs = [];
  const setRef = (ref) => {
    tabRefs.push(ref);
  };

  // Scroll to selected tab. TODO only scroll if not visible
  useEffect(() => {
    isFirstRender.current = false;
  }, [value]);

  const handleSelect = (clickedTab) => {
    if (onSelect) {
      onSelect(clickedTab?.value);
    }

    // Scroll the clicked tab into view
    if (!disableScroll) {
      tabs.forEach((tab, index) => {
        if (tab.value === value) {
          tabRefs?.[index]?.scrollIntoView({
            behavior: isFirstRender?.current ? 'instant' : 'smooth', // So it doesn't awkwardly scroll on first load
            block: 'center',
            inline: 'nearest',
          });
        }
      });
    }
  };

  const handleKeyPress = (event) => {
    tabs.forEach((tab, index) => {
      const tabRef = tabRefs[index];

      // Return early if this is not the active tab
      if (document.activeElement !== tabRef) {
        return;
      }

      // Blur on escape
      if (event.key === 'Escape') {
        document.activeElement.blur();
      }

      // If press enter, select the hovered item
      if (event.key === 'Enter') {
        handleSelect(tab);
      }
    });
  };

  // Add keyboard event listener to input
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [tabs]);

  const tabBarStyles = {
    container: {
      ...style,
      ...style?.container,
    },
    containerInner: {
      display: 'flex',
      columnGap: '20px',
      fontSize: '20px',
      lineHeight: '24px',
      ...style?.containerInner,
    },
    tabContainer: {
      display: 'flex',
      cursor: 'pointer',
      ...style?.tabContainer,
    },
    tab: {
      // fontWeight: '400',
      userSelect: 'none',
      color: '#2E2E2E',
      ...style?.tab,
    },
    tabActive: {
      color: '#E54439',
      fontWeight: 'bold',
      // textDecoration: "underline",
      borderBottom: '2px solid',
      paddingBottom: '3px',
      ...style?.tabActive,
    },
    tabHover: {
      color: '#E54439',
      filter: 'brightness(95%)',
      ...style?.tabHover,
    },
  };

  return (
    <Box className={`tab-bar-container ${className}`} style={tabBarStyles?.container}>
      <Box className='tab-bar-container-inner' style={tabBarStyles?.containerInner}>
        {tabs.map((tab, index) => (
          <Box
            className='tab-container'
            style={{
              ...tabBarStyles?.tabContainer,
            }}
            key={`tab-${tab?.value}`}
            ref={setRef}
            tabIndex={0}
            onMouseEnter={() => setHoveredTab(tab?.value)}
            onMouseLeave={() => setHoveredTab(null)}
          >
            <Box
              className={`tab ${value === tab?.value && 'tab-active'}`}
              style={{
                ...tabBarStyles.tab,
                ...(hoveredTab === tab?.value && tabBarStyles.tabHover),
                ...(value === tab?.value && tabBarStyles?.tabActive),
              }}
              onClick={(e) => {
                handleSelect(tab);
              }}
            >
              {tab.label}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

TabBar.defaultProps = {
  className: '',
  style: {},
  InputProps: {},
  tabs: [],
  value: null,
  onSelect: () => {},
  disableScroll: false,
};

TabBar.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  InputProps: PropTypes.shape({}),
  onSelect: PropTypes.func,
  disableScroll: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
      label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({}),
        PropTypes.number,
        PropTypes.node,
      ]),
    }),
  ),
};
